// ── useConversations ───────────────────────────────────────────────
//
// Manages the inbox list state — filters, search, fetch, and
// realtime-driven re-fetch. Returns a stable API the
// ConversationList component consumes.
//
// Re-fetch is debounced from realtime events: every `wa:message:new`,
// `wa:message:status`, `wa:conversation:*` event marks the list as
// stale; a short debounce timer batches bursts so a 50-message
// inbound flood does ONE refetch, not 50.

import { useCallback, useEffect, useRef, useState } from 'react';
import { realtime } from '@/lib/realtime';
import {
  ConversationRow,
  ConversationStatus,
  listConversations,
} from '../api/whatsapp.api';

export interface ConversationsFilter {
  whatsappAccountId?: string;
  status?: ConversationStatus;
  assignedAgentId?: string;
  search?: string;
  hasUnread?: boolean;
}

const REFETCH_DEBOUNCE_MS = 800;
// Trigger realtime re-fetches for these events.
const INVALIDATING_EVENTS = new Set([
  'wa:message:new',
  'wa:message:status',
  'wa:conversation:read',
  'wa:conversation:status',
  'wa:conversation:assigned',
  'wa:conversation:unassigned',
  'wa:conversation:lead_linked',
  'wa:conversation:lead_unlinked',
  'wa:note:added',
]);

export function useConversations(filter: ConversationsFilter) {
  const [rows, setRows] = useState<ConversationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalUnread, setTotalUnread] = useState(0);

  // Stable key for the debounced refetch. Re-run when filter changes.
  const filterKey = JSON.stringify(filter);
  const debounceRef = useRef<number | null>(null);

  const fetchNow = useCallback(async () => {
    setError(null);
    try {
      const res = await listConversations({ ...filter, page: 1, limit: 200 });
      setRows(res.data);
      setTotalUnread(res.data.reduce((acc, r) => acc + (r.unreadCount ?? 0), 0));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // Initial + filter-change fetch.
  useEffect(() => {
    setLoading(true);
    fetchNow();
  }, [fetchNow]);

  // Realtime → debounced invalidate.
  useEffect(() => {
    const off = realtime.subscribeAll((evt) => {
      if (!INVALIDATING_EVENTS.has(evt.type)) return;
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => {
        fetchNow();
      }, REFETCH_DEBOUNCE_MS);
    });
    return () => {
      off();
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [fetchNow]);

  return { rows, loading, error, totalUnread, refresh: fetchNow };
}
